import { parsePercentInput } from '../../shared/numeric/localeNumeric';
import { bulkCoefficientFromPercent } from '../../shared/numeric/public-api';
import { bulkPreviewScopeHint, isBulkPreviewScopeAllowed } from '../../catalog/app-api';
import { effectiveProductSearchQuery } from '../../shared/product-search/productSearchUx';
import { countPhysicalCountDiffs } from './physicalCountCore';
import type { BulkApplyScope, BulkPreviewScope, InventoryFlowResult, PhysicalCountRow } from './inventoryOperations.types';

export function parseBulkPreviewScope(
  ctx: { canEdit: boolean; branchId: string | null },
  scope: BulkPreviewScope
): InventoryFlowResult<{ coefficient: number; query: ReturnType<typeof buildBulkPreviewQuery> }> {
  if (!ctx.canEdit) {
    return {
      kind: 'blocked',
      notice: {
        tone: 'info',
        title: 'Solo lectura',
        message: 'Podés ver el stock, no actualizar por porcentaje.',
      },
    };
  }
  const coef = bulkCoefficientFromPercent(scope.bulkPercentInput);
  const pctNum = parsePercentInput(scope.bulkPercentInput);
  if (pctNum === null) {
    return {
      kind: 'blocked',
      notice: {
        tone: 'error',
        title: 'Porcentaje inválido',
        message: 'Ingresá un número (ej. 5 para subir precios o cantidades un 5%).',
      },
    };
  }
  if (!isBulkPreviewScopeAllowed(scope.bulkCategory, scope.bulkSearch)) {
    return { kind: 'blocked', notice: { tone: 'info', title: 'Acotá el alcance', message: bulkPreviewScopeHint } };
  }
  if (scope.bulkApplyTo === 'quantities' && !ctx.branchId) {
    return {
      kind: 'blocked',
      notice: { tone: 'error', title: 'Elegí una sucursal', message: 'Seleccioná una sucursal para actualizar cantidades.' },
    };
  }
  return {
    kind: 'done',
    refresh: {},
    data: { coefficient: coef, query: buildBulkPreviewQuery(ctx.branchId, scope) },
  };
}

export function parseBulkApplyScope(
  ctx: { canEdit: boolean; branchId: string | null },
  scope: BulkApplyScope
): InventoryFlowResult<{ coefficient: number; productIds: string[]; applyTo: BulkApplyScope['bulkApplyTo'] }> {
  const preview = parseBulkPreviewScope(ctx, scope);
  if (preview.kind === 'blocked') return preview;
  if (scope.preview.length === 0) {
    return { kind: 'blocked', notice: { tone: 'info', title: 'Sin vista previa', message: 'Mirá qué cambia antes de aplicar.' } };
  }
  return {
    kind: 'done',
    refresh:
      scope.bulkApplyTo === 'quantities'
        ? { stocks: true, branchActivity: true, dashboard: true }
        : {},
    data: {
      coefficient: preview.data!.coefficient,
      productIds: scope.preview.map((p) => p.id),
      applyTo: scope.bulkApplyTo,
    },
  };
}

function buildBulkPreviewQuery(branchId: string | null, scope: BulkPreviewScope) {
  return {
    categoryId: scope.bulkCategory,
    search: effectiveProductSearchQuery(scope.bulkSearch.trim()),
    branchId: branchId ?? undefined,
    coefficient: bulkCoefficientFromPercent(scope.bulkPercentInput),
    applyTo: scope.bulkApplyTo,
    limit: 500,
  };
}

export function canOpenBulkCoefficient(ctx: { canEdit: boolean }): InventoryFlowResult {
  if (!ctx.canEdit) {
    return {
      kind: 'blocked',
      notice: {
        tone: 'info',
        title: 'Solo lectura',
        message: 'Podés ver el stock, no actualizar por porcentaje.',
      },
    };
  }
  return { kind: 'done', refresh: {} };
}

export function canOpenPhysicalCount(ctx: { canEdit: boolean; canCount?: boolean; branchId: string | null }): InventoryFlowResult {
  if (!ctx.canEdit) {
    return {
      kind: 'blocked',
      notice: {
        tone: 'info',
        title: 'Solo lectura',
        message: 'Podés ver el stock, no hacer el conteo.',
      },
    };
  }
  if (!ctx.canCount) {
    return {
      kind: 'blocked',
      notice: {
        tone: 'info',
        title: 'Sin permiso',
        message: 'No tenés permiso para hacer conteo físico.',
      },
    };
  }
  if (!ctx.branchId) {
    return {
      kind: 'blocked',
      notice: { tone: 'error', title: 'Elegí una sucursal', message: 'Seleccioná una sucursal.' },
    };
  }
  return { kind: 'done', refresh: {} };
}

export function parsePhysicalCountApply(
  ctx: { canEdit: boolean; branchId: string | null },
  rows: PhysicalCountRow[]
): InventoryFlowResult<{ branchId: string; diffCount: number }> {
  const gate = canOpenPhysicalCount(ctx);
  if (gate.kind === 'blocked') return gate;
  const diffCount = countPhysicalCountDiffs(rows);
  if (diffCount === 0) {
    return {
      kind: 'blocked',
      notice: {
        tone: 'info',
        title: 'Sin diferencias',
        message: 'No hay diferencias con lo cargado, o algún número no es válido.',
      },
    };
  }
  return {
    kind: 'done',
    refresh: { stocks: true, branchActivity: true, dashboard: true },
    data: { branchId: ctx.branchId!, diffCount },
  };
}
